import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import type {
  HomepageV2TasksQuery,
  HomepageV2TasksQueryVariables,
  HomepageV2TasksQuery_processTasks_ProcessTaskNode as ProcessTaskNode,
} from './__generated/HomepageV2TasksQuery';
import {MyWorkCategory} from './MyWorkConstants';
import {taskRowProps} from './MyWorkRowHelpers';
import {MyWorkRowsFooterAction, MyWorkRowsList} from './MyWorkRowsList';
import {useMyWorkRows} from './useMyWorkRows';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

export const HOMEPAGE_TASKS_QUERY = gql`
  query HomepageV2TasksQuery($taskIds: [StrictString!]!) @skipAudit @engComponent(name: NAVIGATION) {
    processTasks(ids: $taskIds) {
      ... on ProcessTaskNode {
        id
        name
        modifiedAt
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
            statusType
          }
        }
        url
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

/** Fetches and lists process tasks for the selected task-status filter. */
export default function TasksRows({
  footerAction,
  hasCategoryWork,
  selectedTaskIds,
}: {
  footerAction?: MyWorkRowsFooterAction;
  hasCategoryWork: boolean;
  selectedTaskIds: Array<string> | null;
}): JSX.Element {
  const rowsResult = useMyWorkRows<HomepageV2TasksQuery, HomepageV2TasksQueryVariables, ProcessTaskNode>({
    query: HOMEPAGE_TASKS_QUERY,
    variables: {taskIds: selectedTaskIds!},
    skip: selectedTaskIds == null,
    getItems: (data) => data.processTasks,
    toRowProps: (item) => taskRowProps(item),
    errorMessage: 'Error loading tasks',
    testComponent: TEST_COMPONENT,
  });

  if (rowsResult.type !== QueryResultType.SUCCESS) {
    return rowsResult.element;
  }

  return (
    <MyWorkRowsList
      rows={rowsResult.rows}
      category={MyWorkCategory.TASKS}
      hasCategoryWork={hasCategoryWork}
      footerAction={footerAction}
      emptyMessageDataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'messageRow',
        key: 'emptyTasks',
      }}
    />
  );
}
